import { useState, useEffect } from 'react';
import { Star, ThumbsUp, Shield, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Display Component
const StarRating = ({ rating, size = 'md' }) => {
  const sizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizes[size]} ${
            star <= rating 
              ? 'fill-yellow-400 text-yellow-400' 
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

// Interactive Star Rating Input
const StarRatingInput = ({ rating, setRating }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            className={`w-8 h-8 ${
              star <= (hover || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Individual Review Card
const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const isLongReview = review.review_text.length > 200;
  
  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-[#1a1a1a]">{review.reviewer_name}</span>
            {review.is_verified && (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <Shield className="w-3 h-3" />
                Verified Purchase
              </span>
            )}
          </div>
          {review.company_name && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Building2 className="w-3 h-3" />
              {review.company_name}
            </div>
          )}
        </div>
        <StarRating rating={review.rating} size="sm" />
      </div>
      
      {review.title && (
        <h4 className="font-medium text-[#1a1a1a] mb-2">{review.title}</h4>
      )}
      
      <p className="text-gray-600 text-sm leading-relaxed">
        {isLongReview && !expanded 
          ? review.review_text.slice(0, 200) + '...' 
          : review.review_text}
      </p>
      
      {isLongReview && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-[#c5a059] text-sm mt-2 hover:underline"
        >
          {expanded ? (
            <>Show less <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>Read more <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
      
      <div className="text-xs text-gray-400 mt-3">
        {new Date(review.created_at).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })}
      </div>
    </div>
  );
};

// Review Submission Form
const ReviewForm = ({ productId, productName, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    review_text: '',
    reviewer_name: '',
    reviewer_email: '',
    company_name: '',
    order_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (formData.review_text.length < 20) {
      setError('Review must be at least 20 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          product_name: productName,
          ...formData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit review');
      }
      
      setSuccess(data);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ThumbsUp className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Thank You!</h3>
        <p className="text-gray-600 mb-4">
          Your review has been submitted and will appear after approval.
        </p>
        <div className="bg-[#c5a059]/10 border border-[#c5a059] rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700 mb-2">Your appreciation coupon:</p>
          <p className="text-2xl font-bold text-[#c5a059] tracking-wider">{success.coupon_code}</p>
          <p className="text-sm text-gray-600 mt-1">2% OFF - Check your email for details!</p>
        </div>
        <Button onClick={onClose} variant="outline">Close</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Rating *
        </label>
        <StarRatingInput rating={formData.rating} setRating={(r) => setFormData({...formData, rating: r})} />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Name *
          </label>
          <input
            type="text"
            required
            value={formData.reviewer_name}
            onChange={(e) => setFormData({...formData, reviewer_name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c5a059] focus:border-transparent"
            placeholder="John Doe"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Email *
          </label>
          <input
            type="email"
            required
            value={formData.reviewer_email}
            onChange={(e) => setFormData({...formData, reviewer_email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c5a059] focus:border-transparent"
            placeholder="john@company.com"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name <span className="text-gray-400">(Optional)</span>
          </label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({...formData, company_name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c5a059] focus:border-transparent"
            placeholder="Company Pvt. Ltd."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Order ID <span className="text-gray-400">(For verified badge)</span>
          </label>
          <input
            type="text"
            value={formData.order_id}
            onChange={(e) => setFormData({...formData, order_id: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c5a059] focus:border-transparent"
            placeholder="ORD-XXXXX"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Review Title <span className="text-gray-400">(Optional)</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c5a059] focus:border-transparent"
          placeholder="Great quality and service!"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Your Review * <span className="text-gray-400">(Min 20 characters)</span>
        </label>
        <textarea
          required
          rows={4}
          value={formData.review_text}
          onChange={(e) => setFormData({...formData, review_text: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c5a059] focus:border-transparent resize-none"
          placeholder="Share your experience with this product..."
        />
        <p className="text-xs text-gray-400 mt-1">
          {formData.review_text.length}/20 characters minimum
        </p>
      </div>
      
      {error && (
        <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
          {error}
        </div>
      )}
      
      <div className="flex gap-3">
        <Button 
          type="submit" 
          disabled={loading}
          className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a]"
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
      
      <p className="text-xs text-gray-500">
        By submitting, you'll receive a 2% appreciation coupon via email.
      </p>
    </form>
  );
};

// Main Product Reviews Section
export const ProductReviews = ({ productId, productName }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/product/${productId}`);
      const data = await response.json();
      setReviews(data.reviews || []);
      setStats({
        total_reviews: data.total_reviews || 0,
        average_rating: data.average_rating || 0
      });
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const displayedReviews = showAll ? reviews : reviews.slice(0, 3);

  return (
    <section className="py-12 bg-gray-50" data-testid="product-reviews">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-[#1a1a1a]" >
              Customer Reviews
            </h2>
            {stats.total_reviews > 0 && (
              <div className="flex items-center gap-3 mt-2">
                <StarRating rating={Math.round(stats.average_rating)} size="lg" />
                <span className="text-lg font-semibold text-[#1a1a1a]">
                  {stats.average_rating}
                </span>
                <span className="text-gray-500">
                  ({stats.total_reviews} {stats.total_reviews === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            )}
          </div>
          
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white"
            data-testid="write-review-btn"
          >
            Write a Review
          </Button>
        </div>

        {/* Review Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-4">
                Write a Review for {productName}
              </h3>
              <ReviewForm 
                productId={productId}
                productName={productName}
                onSuccess={() => {
                  fetchReviews();
                }}
                onClose={() => setShowForm(false)}
              />
            </div>
          </div>
        )}

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No reviews yet. Be the first to review this product!</p>
            <Button
              onClick={() => setShowForm(true)}
              variant="outline"
              className="border-[#c5a059] text-[#c5a059] hover:bg-[#c5a059] hover:text-[#1a1a1a]"
            >
              Write the First Review
            </Button>
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {displayedReviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
            
            {reviews.length > 3 && (
              <div className="text-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowAll(!showAll)}
                  className="border-gray-300"
                >
                  {showAll ? 'Show Less' : `View All ${reviews.length} Reviews`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export { StarRating, StarRatingInput, ReviewCard, ReviewForm };
